import React, { forwardRef } from 'react';
import {
  Button as UiButton,
  type ButtonProps as UiButtonProps,
  type ButtonVariant,
} from '../ui/Button';

/**
 * Compatibility wrapper around ui/Button.
 * Maps the legacy common/Button API (isLoading, outline, etc.) onto the canonical Button.
 */
interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  loadingText?: string;
  fullWidth?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  children?: React.ReactNode;
}

const VARIANT_MAP: Record<
  NonNullable<ButtonProps['variant']>,
  ButtonVariant
> = {
  primary: 'primary',
  secondary: 'secondary',
  danger: 'danger',
  ghost: 'ghost',
  outline: 'outline',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant = 'primary',
      size = 'md',
      isLoading = false,
      loadingText,
      fullWidth = false,
      icon,
      iconPosition = 'left',
      className,
      disabled,
      ...props
    },
    ref,
  ) => {
    const uiProps: UiButtonProps = {
      variant: VARIANT_MAP[variant],
      size,
      loading: isLoading,
      fullWidth,
      icon,
      iconPosition,
      className,
      disabled: disabled || isLoading,
      children: isLoading && loadingText ? loadingText : children,
      ...props,
    };

    return <UiButton ref={ref} {...uiProps} />;
  },
);

Button.displayName = 'Button';
